import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Box,
  Alert
} from '@mui/material';
import type { Experiment } from '../../types/experiment';

interface PauseExperimentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (temperature: number) => void;
  experiment: Experiment | null;
  currentTemperature: number | null;
  isLoading?: boolean;
}

const PauseExperimentDialog: React.FC<PauseExperimentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  experiment,
  currentTemperature,
  isLoading = false
}) => {
  const [enableDrift, setEnableDrift] = useState(false);
  const [temperature, setTemperature] = useState(0);
  const [temperatureError, setTemperatureError] = useState<string>('');

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setEnableDrift(false);
      setTemperature(currentTemperature || 0);
      setTemperatureError('');
    }
  }, [open, currentTemperature]);

  // Validate temperature when it changes
  useEffect(() => {
    if (enableDrift) {
      if (temperature === null || temperature === undefined || isNaN(temperature)) {
        setTemperatureError('Please enter a temperature');
      } else if (temperature < 0) {
        setTemperatureError('Temperature must be 0°C or higher');
      } else if (temperature > 45) {
        setTemperatureError('Temperature must be 45°C or lower');
      } else {
        setTemperatureError('');
      }
    } else {
      setTemperatureError('');
    }
  }, [temperature, enableDrift]);

  const handleDriftToggle = (checked: boolean) => {
    setEnableDrift(checked);
    if (!checked) {
      setTemperature(0);
      setTemperatureError('');
    } else {
      setTemperature(currentTemperature || 0);
    }
  };

  const handleConfirm = () => {
    if (enableDrift && temperatureError) {
      return; // Don't proceed if there's a validation error
    }
    onConfirm(enableDrift ? Number(temperature) : 0);
  };

  const isConfirmDisabled = isLoading || (enableDrift && !!temperatureError);

  // Determine if chamber is enabled
  const isChamberEnabled = experiment?.chamber_enabled || false;
  const isCamerasOnly = !isChamberEnabled;

  // Get appropriate warning text
  const getWarningText = () => {
    if (isCamerasOnly) {
      return "This will pause the experiment. Cameras will stop capturing.";
    }
    if (enableDrift) {
      return "This will pause the experiment. Cameras will stop and chamber will enter DRIFT mode.";
    }
    return "This will pause the experiment. Cameras will stop and chamber will enter SLEEP mode.";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pause Experiment</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {getWarningText()}
          </Typography>

          {experiment && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Experiment:</strong> {experiment.exp_id}<br />
                <strong>Test:</strong> {experiment.test_id}<br />
                <strong>Profile:</strong> {experiment.profile_id}
              </Typography>
            </Alert>
          )}

          {/* DRIFT Checkbox - only show if chamber is enabled */}
          {isChamberEnabled && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableDrift}
                  onChange={(e) => handleDriftToggle(e.target.checked)}
                  disabled={isLoading}
                />
              }
              label="Enable DRIFT mode"
              sx={{ mb: 2 }}
            />
          )}

          {/* Temperature Field */}
          <TextField
            label={`DRIFT Temperature${!enableDrift ? ' (SLEEP mode)' : ' (°C)'}`}
            type="number"
            value={temperature}
            onChange={(e) => setTemperature(Number(e.target.value))}
            disabled={!enableDrift || isLoading || isCamerasOnly}
            error={!!temperatureError}
            helperText={
              temperatureError || 
              (enableDrift ? 'Enter temperature between 0-45°C' : 'Chamber will enter SLEEP mode')
            }
            inputProps={{
              min: 0,
              max: 45,
              step: 0.1
            }}
            fullWidth
            sx={{ mb: 2 }}
          />

          {/* Additional info for cameras-only experiments */}
          {isCamerasOnly && (
            <Alert severity="info">
              <Typography variant="body2">
                No chamber is enabled for this experiment. Only cameras will be affected.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={isConfirmDisabled}
        >
          {isLoading ? 'Pausing...' : 'Pause Experiment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PauseExperimentDialog; 